/**
 * A kanban column — droppable, contains a stack of cards.
 */

import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { useSortable } from '@dnd-kit/sortable'
import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import type { ProjectColumn, Task } from '@friday/shared/realtime'
import type { PublicProfile } from '@/features/profiles/profiles'
import { Card } from './Card'
import { cn } from '@/lib/utils'
import { useCreateTask } from '@/features/projects/board'

interface ColumnProps {
  wsId: string
  pId: string
  column: ProjectColumn
  tasks: Task[]
  /** Profiles of the assignees on this board (FRIDAY-75). */
  profiles?: Record<string, PublicProfile>
  onOpenTask: (task: Task) => void
  selectedTaskId?: string | null
}

export function Column({ wsId, pId, column, tasks, profiles, onOpenTask, selectedTaskId }: ColumnProps) {
  // The whole column (header, card list, "Add task" footer) is the drop
  // target, so a full column with no blank body gap still accepts drops.
  const { setNodeRef, isOver } = useDroppable({
    id: `col:${column.id}`,
    data: { type: 'column', columnId: column.id },
  })
  const [composing, setComposing] = useState(false)
  const [draft, setDraft] = useState('')
  const create = useCreateTask(wsId, pId, column.id)

  return (
    <div
      ref={setNodeRef}
      className={cn(
        // `relative isolate z-0` establishes an explicit lower stacking
        // context for the column so the body-portaled dnd-kit DragOverlay
        // (z-index 1200) reliably paints on top during a drag.
        'relative isolate z-0 flex h-full w-72 shrink-0 flex-col rounded-lg bg-surface-100/60 dark:bg-surface-800/40',
        isOver && 'ring-2 ring-brand-500/50',
      )}
    >
      <header className="flex items-center justify-between border-b border-surface-200/60 dark:border-surface-700/60 px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: column.color }} />
          <span className="text-sm font-medium">{column.name}</span>
          <span className="text-xs text-surface-500">{tasks.length}</span>
        </div>
        {column.wipLimit != null && tasks.length > column.wipLimit && (
          <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300">
            WIP {tasks.length}/{column.wipLimit}
          </span>
        )}
      </header>

      <div className="flex-1 space-y-1.5 overflow-y-auto p-2">
        <SortableContext items={tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
          <AnimatePresence initial={false}>
            {tasks.map((t) => (
              <SortableCard
                key={t.id}
                task={t}
                profiles={profiles}
                onOpen={onOpenTask}
                isSelected={selectedTaskId === t.id}
              />
            ))}
          </AnimatePresence>
        </SortableContext>
        {tasks.length === 0 && !composing && (
          <div className="py-6 text-center text-xs text-surface-500">Drop or create a task</div>
        )}
      </div>

      <div className="border-t border-surface-200/60 dark:border-surface-700/60 p-2">
        {composing ? (
          <form
            onSubmit={async (e) => {
              e.preventDefault()
              const t = draft.trim()
              if (!t) return
              await create.mutateAsync({ title: t, priority: 'medium' })
              setDraft('')
              setComposing(false)
            }}
            className="space-y-1.5"
          >
            <textarea
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setComposing(false)
                  setDraft('')
                }
              }}
              placeholder="Task title…"
              className="input min-h-[2.5rem] resize-none text-sm"
              rows={2}
            />
            <div className="flex items-center justify-end gap-1.5">
              <button type="button" className="btn-ghost text-xs" onClick={() => setComposing(false)}>
                Cancel
              </button>
              <button type="submit" className="btn-primary text-xs" disabled={!draft.trim() || create.isPending}>
                {create.isPending ? 'Creating…' : 'Add task'}
              </button>
            </div>
          </form>
        ) : (
          <button
            onClick={() => setComposing(true)}
            className="w-full rounded-md border border-dashed border-surface-300 dark:border-surface-700 px-2 py-1.5 text-left text-xs text-surface-500 hover:border-brand-500 hover:text-brand-600 dark:hover:text-brand-300"
          >
            + Add task
          </button>
        )}
      </div>
    </div>
  )
}

/** Sortable wrapper around the draggable Card. */
function SortableCard(props: {
  task: Task
  profiles?: Record<string, PublicProfile>
  onOpen: (t: Task) => void
  isSelected?: boolean
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.task.id,
    // Surface the task via `e.active.data.current.task` so the DndContext's
    // onDragStart can hand the same Task to the <DragOverlay> preview.
    // `type: 'task'` lets the Board resolve a card-over drop to the card's
    // column without treating the task ID as a column ID.
    data: { type: 'task', task: props.task },
  })
  return (
    <motion.div
      ref={setNodeRef}
      // No `touch-none`: an immediate swipe must stay a native scroll. The
      // Board's delayed TouchSensor claims only an intentional press-and-hold.
      className="touch-manipulation"
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: isDragging ? 0.4 : 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.15 }}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      {...attributes}
      {...listeners}
    >
      <Card
        task={props.task}
        profiles={props.profiles}
        onOpen={props.onOpen}
        isSelected={props.isSelected}
        isDragging={isDragging}
      />
    </motion.div>
  )
}

// CSS helper re-export to keep imports tidy in this folder.
import { CSS } from '@dnd-kit/utilities'
